import React, { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Workspace, type WorkspaceProps } from './Workspace';
import { isModeLocked } from './modes';
import {
  WORKSPACE_MODES,
  isValidMode,
  readWorkspaceParams,
  writeWorkspaceParams,
  type WorkspaceMode,
  type WorkspaceParamsUpdate,
} from './urlState';

/**
 * WorkspaceRoute: binds the Workspace shell to the URL.
 *
 * The Mode and the selection (`loan`, `doc`, `field`, `exception`) live in the
 * query string. Changing Mode rewrites only `mode`, so the selection survives
 * (Requirements 3.3, 3.4).
 *
 * Job loading (tasks 2.2, 2.3) and the Row → Mode default / locked-Mode rules
 * (task 2.4) plug in through the props below: `title`, `statusLabel`,
 * `defaultMode`, `locked`, `rail` and `children` (the Mode body).
 */
export interface WorkspaceRouteProps
  extends Omit<
    WorkspaceProps,
    'mode' | 'onModeChange' | 'userName' | 'selection' | 'onNavigateToAnchor'
  > {
  /** Mode used when the URL has no valid `mode` (Row → Mode default). */
  defaultMode?: WorkspaceMode;
}

export const WorkspaceRoute: React.FC<WorkspaceRouteProps> = ({
  feature,
  defaultMode,
  locked,
  ...rest
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();

  const mode = useMemo(() => {
    const { mode: fromUrl } = readWorkspaceParams(searchParams, feature, defaultMode);
    if (!isModeLocked(locked, fromUrl)) return fromUrl;
    // A locked Mode in the URL (for example a shared link before the TR review
    // is finalized) is never the active one: use the default, or the first
    // Mode that is open (Requirement 3.5).
    const fallback = [defaultMode, ...WORKSPACE_MODES[feature]].find(
      (m): m is WorkspaceMode => !!m && isValidMode(feature, m) && !isModeLocked(locked, m)
    );
    return fallback ?? fromUrl;
  }, [searchParams, feature, defaultMode, locked]);

  // Selection in the URL; new comments anchor to it (Requirement 12.3).
  const selection = useMemo(() => {
    const { mode: _mode, ...rest } = readWorkspaceParams(searchParams, feature, defaultMode);
    return rest;
  }, [searchParams, feature, defaultMode]);

  const handleModeChange = useCallback(
    (next: WorkspaceMode) => {
      setSearchParams(writeWorkspaceParams(searchParams, { mode: next }));
    },
    [searchParams, setSearchParams]
  );

  // Selecting a comment's Anchor rewrites only the keys the Anchor sets.
  const handleNavigateToAnchor = useCallback(
    (update: WorkspaceParamsUpdate) => {
      setSearchParams(writeWorkspaceParams(searchParams, update));
    },
    [searchParams, setSearchParams]
  );

  return (
    <Workspace
      {...rest}
      feature={feature}
      locked={locked}
      mode={mode}
      selection={selection}
      onModeChange={handleModeChange}
      onNavigateToAnchor={handleNavigateToAnchor}
      userName={currentUser?.name}
    />
  );
};

export default WorkspaceRoute;
